import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    listAdminProducts,
    listAdminCategories,
    deleteAdminProduct,
    duplicateAdminProduct,
} from "@/lib/api";
import { Plus, Search, Pencil, Trash2, Copy, Loader2, Pin } from "lucide-react";
import { toast } from "sonner";

const Products = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState("");
    const [deleteId, setDeleteId] = useState(null);

    const { data: products = [], isLoading } = useQuery({
        queryKey: ["admin", "products"],
        queryFn: listAdminProducts,
    });
    const { data: categories = [] } = useQuery({
        queryKey: ["admin", "categories"],
        queryFn: listAdminCategories,
    });

    const deleteMutation = useMutation({
        mutationFn: deleteAdminProduct,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin", "products"] });
            queryClient.invalidateQueries({ queryKey: ["catalog"] });
            toast.success("Produit supprimé.");
            setDeleteId(null);
        },
        onError: () => toast.error("Impossible de supprimer le produit."),
    });

    const duplicateMutation = useMutation({
        mutationFn: duplicateAdminProduct,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin", "products"] });
            queryClient.invalidateQueries({ queryKey: ["catalog"] });
            toast.success("Produit dupliqué.");
        },
        onError: () => toast.error("Impossible de dupliquer le produit."),
    });

    const categoryLabel = (name) =>
        categories.find((c) => c.label === name)?.label || name;

    const filtered = products.filter((p) =>
        p.name.toLowerCase().includes(search.toLowerCase()),
    );

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="font-display text-3xl font-semibold tracking-tight">
                            Produits
                        </h1>
                        <p className="text-neutral-500 mt-1">
                            Gérez le catalogue affiché sur le site.
                        </p>
                    </div>
                    <Button onClick={() => navigate("/admin/products/new")}>
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter un produit
                    </Button>
                </div>

                <div className="flex items-center gap-3">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Rechercher un produit…"
                            className="pl-9"
                        />
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Nom</TableHead>
                                <TableHead>Catégorie</TableHead>
                                <TableHead>Statut</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center py-8">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#d4af37]" />
                                    </TableCell>
                                </TableRow>
                            ) : filtered.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center py-8 text-neutral-500">
                                        Aucun produit trouvé.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filtered.map((p) => (
                                    <TableRow key={p.id}>
                                        <TableCell className="font-medium">
                                            <div className="flex items-center gap-2">
                                                {p.name}
                                                {p.pinned && (
                                                    <Pin className="w-3.5 h-3.5 text-[#d4af37] fill-[#d4af37]" />
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>{categoryLabel(p.category)}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                {p.active ? (
                                                    <Badge variant="secondary">Actif</Badge>
                                                ) : (
                                                    <Badge variant="outline">Inactif</Badge>
                                                )}
                                                {p.top_selling && (
                                                    <Badge className="bg-gradient-to-r from-[#5b21b6] to-[#d4af37] text-white border-0">
                                                        Top vente
                                                    </Badge>
                                                )}
                                                {p.pinned && (
                                                    <Badge variant="outline" className="text-[#d4af37] border-[#d4af37]">
                                                        Épinglé
                                                    </Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => navigate(`/admin/products/${p.id}/edit`)}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => duplicateMutation.mutate(p.id)}
                                                    disabled={duplicateMutation.isPending}
                                                    title="Dupliquer"
                                                >
                                                    {duplicateMutation.isPending ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <Copy className="w-4 h-4" />
                                                    )}
                                                </Button>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    className="text-red-600 hover:text-red-700"
                                                    onClick={() => setDeleteId(p.id)}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Supprimer le produit ?</DialogTitle>
                        <DialogDescription>
                            Cette action est irréversible. Le produit sera définitivement supprimé du catalogue.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setDeleteId(null)}>
                            Annuler
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => deleteMutation.mutate(deleteId)}
                            disabled={deleteMutation.isPending}
                        >
                            {deleteMutation.isPending && (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            )}
                            Supprimer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
};

export default Products;
